import type { Row } from '@tanstack/react-table'
import type { DocumentPreviewPanelProps } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import {
  DocumentTableColumns,
  type DocumentTableRow,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentPreviewPanelState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentPreviewPanelState/useDocumentPreviewPanelState'
import { useDocumentResultsAnalytics } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsAnalytics'
import {
  useDocumentResultsFilterState,
  type UseDocumentResultsFilterStateResult,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsFilterState'
import { useDocumentSearchNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchNavigation'
import type { FacetFilterId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFacets'
import type {
  DocumentSearchResult,
  DrawingRevisionOption,
  SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { DocumentTableColumnUniqueValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentTablePage'
import type { SearchActionsContextType } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export interface UseDocumentResultsViewStateParams {
  activeTabId: string
  documentResults: DocumentTableRow[]
  facetColumnValues?: Partial<Record<FacetFilterId, string[]>>
  isGeckoUser: boolean
  isLinkedDocsMode: boolean
  rawSearchData: DocumentSearchResult | undefined
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  searchState: SearchHistoryItem['searchState']
  selectedDocumentId: string | null
  siteId: string
  contentFilter: DocumentsViewerContentFilter
  viewMode: ViewMode
  onSearchStateChange: (updates: SearchStateUpdate) => void
  onSelectDocument: SearchActionsContextType['onSelectDocument']
  onOpenNewTab: SearchActionsContextType['onOpenNewTab']
  onAddRecentDocument: SearchActionsContextType['onAddRecentDocument']
  onSnapshotSearchResults: SearchActionsContextType['onSnapshotSearchResults']
  setSelectedDocumentId: (id: string) => void
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void
}

export interface UseDocumentResultsViewStateResult {
  activeDocument: DocumentTableRow | null
  activeIndex: number
  cardFilters: CardFilterValues
  commitCardFiltersToUrl: () => void
  contentAreaRef: React.MutableRefObject<HTMLDivElement | null>
  documentPreviewPanelProps: Omit<DocumentPreviewPanelProps, 'onAddToContext'>
  filteredDocuments: DocumentTableRow[]
  handleCardFiltersChange: (next: CardFilterValues) => void
  handleOpenPreview: () => void
  handleRowFocus: (row: Row<DocumentTableRow>) => void
  handleTableFiltersChange: UseDocumentResultsFilterStateResult['handleTableFiltersChange']
  hasActiveFilters: boolean
  isLoadingPreview: boolean
  isPreviewOpen: boolean
  onSearchDocumentNavigate: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean,
    target?: { pageNumber?: number; pageNum?: number; fragmentText?: string }
  ) => void
  onViewModeChange: (value: string | number) => void
  setActivePreviewDocumentOverride: (document: DocumentTableRow | null) => void
  setActivePreviewPage: (page: number) => void
  selectedCardKey: string | null
  setSelectedCardKey: React.Dispatch<React.SetStateAction<string | null>>
  selectedRevisionByCardKey: Map<string, DrawingRevisionOption>
  setSelectedRevisionByCardKey: React.Dispatch<
    React.SetStateAction<Map<string, DrawingRevisionOption>>
  >
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  setActiveIndexWithoutPreview: React.Dispatch<React.SetStateAction<number>>
  tableColumns: ReturnType<typeof DocumentTableColumns>
  tableColumnUniqueValues: DocumentTableColumnUniqueValues
  tableFilters: UseDocumentResultsFilterStateResult['tableFilters']
  tableHasActiveFilters: boolean
}

export function useDocumentResultsViewState({
  activeTabId,
  contentFilter,
  documentResults,
  facetColumnValues,
  handleViewModeChange,
  isGeckoUser,
  isLinkedDocsMode,
  onAddRecentDocument,
  onOpenNewTab,
  onSearchStateChange,
  onSelectDocument,
  onSnapshotSearchResults,
  rawSearchData,
  searchEngine,
  searchLatencyMs,
  searchState,
  selectedDocumentId,
  setSelectedDocumentId,
  siteId,
  viewMode,
}: UseDocumentResultsViewStateParams): UseDocumentResultsViewStateResult {
  const {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    filteredDocuments,
    handleCardFiltersChange,
    handleTableFiltersChange,
    hasActiveFilters,
    onViewModeChange,
    setActiveIndex,
    tableColumns,
    tableColumnUniqueValues,
    tableFilters,
    tableHasActiveFilters,
  } = useDocumentResultsFilterState({
    documentResults,
    facetColumnValues,
    handleViewModeChange,
    onSearchStateChange,
    isServerPaged: searchState.browseAll === true,
    searchQuery: searchState.query,
    searchStateFilters: searchState.filters,
    selectedDocumentId,
    viewMode,
  })
  const [activePreviewPage, setActivePreviewPage] = React.useState(1)
  const [activePreviewDocumentOverride, setActivePreviewDocumentOverride] =
    React.useState<DocumentTableRow | null>(null)
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(null)
  const [selectedRevisionByCardKey, setSelectedRevisionByCardKey] = useState<
    Map<string, DrawingRevisionOption>
  >(new Map())

  useEffect(() => {
    setSelectedCardKey(null)
    setSelectedRevisionByCardKey(new Map())
  }, [searchState.query])

  const contentAreaRef = useRef<HTMLDivElement>(null)

  const onSearchDocumentNavigate = useDocumentSearchNavigation({
    activeTabId,
    cardFilters,
    documentResults,
    isGeckoUser,
    onAddRecentDocument,
    onOpenNewTab,
    onSelectDocument,
    onSnapshotSearchResults,
    rawSearchData,
    searchQuery: searchState.query,
    siteId,
  })

  const {
    activeDocument,
    documentPreviewPanelProps,
    handleOpenPreview,
    isLoadingPreview,
    isPreviewOpen,
  } = useDocumentPreviewPanelState({
    activeIndex,
    contentFilter,
    documentResults,
    filteredDocuments,
    isLinkedDocsMode,
    onSearchDocumentNavigate,
    onSwitchPreviewDocument: (doc, pageNum) => {
      setActivePreviewDocumentOverride(doc)
      setActivePreviewPage(pageNum)
    },
    selectedDocumentId,
    activeDocumentOverride: activePreviewDocumentOverride,
    initialPage: activePreviewPage,
    viewMode,
  })

  const setActiveIndexWithoutPreview: React.Dispatch<
    React.SetStateAction<number>
  > = useCallback(
    (nextActiveIndex) => {
      setActivePreviewPage(1)
      setActivePreviewDocumentOverride(null)
      setActiveIndex(nextActiveIndex)
    },
    [setActiveIndex]
  )

  const handleActiveIndexChange: React.Dispatch<React.SetStateAction<number>> =
    useCallback(
      (nextActiveIndex) => {
        handleOpenPreview()
        setActiveIndexWithoutPreview(nextActiveIndex)
      },
      [handleOpenPreview, setActiveIndexWithoutPreview]
    )

  useDocumentResultsAnalytics({
    activeDocument,
    documentResults,
    rawSearchData,
    searchEngine,
    searchLatencyMs,
    searchQuery: searchState.query,
    siteId,
  })

  const handleRowFocus = useCallback(
    (row: Row<DocumentTableRow>) => {
      setSelectedDocumentId(row.original.id)
    },
    [setSelectedDocumentId]
  )

  useEffect(() => {
    if (viewMode !== 'table' || !selectedDocumentId || !contentAreaRef.current)
      return
    const id = selectedDocumentId
    const rowSelector = `[data-row-id="${CSS.escape(id)}"]`
    const rafId = requestAnimationFrame(() => {
      const row = contentAreaRef.current?.querySelector(
        rowSelector
      ) as HTMLElement | null
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return () => cancelAnimationFrame(rafId)
  }, [viewMode, selectedDocumentId])

  return {
    activeDocument,
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    contentAreaRef,
    documentPreviewPanelProps,
    filteredDocuments,
    handleCardFiltersChange,
    handleOpenPreview,
    handleRowFocus,
    handleTableFiltersChange,
    hasActiveFilters,
    isLoadingPreview,
    isPreviewOpen,
    onSearchDocumentNavigate,
    onViewModeChange,
    setActivePreviewDocumentOverride,
    setActivePreviewPage,
    selectedCardKey,
    setSelectedCardKey,
    selectedRevisionByCardKey,
    setSelectedRevisionByCardKey,
    setActiveIndex: handleActiveIndexChange,
    setActiveIndexWithoutPreview,
    tableColumns,
    tableColumnUniqueValues,
    tableFilters,
    tableHasActiveFilters,
  }
}
